import type {
  CanonicalEdge,
  CanonicalNode,
  CanonicalWorkflowDocument,
} from '@los-workspace/workflow-runtime';
import { coerceText } from './coerce-text';

export type OpportunityWorkflowRoles = {
  listNodeKey: string | null;
  applicantTypeNodeKey: string | null;
  statusNodeKeys: {
    individual: string | null;
    corporate: string | null;
  };
  searchNodeKeys: string[];
  createNodeKeys: string[];
  viewNodeKeys: string[];
  editNodeKeys: string[];
  ifElseFieldCode: string | null;
};

type EdgeCondition = {
  fieldCode?: string;
  value?: unknown;
};

function nodeTypeOf(node: CanonicalNode): string {
  return coerceText(node.nodeType).toLowerCase();
}

function readCondition(edge: CanonicalEdge): EdgeCondition {
  const condition = edge.condition;
  if (!condition || typeof condition !== 'object' || Array.isArray(condition)) {
    return {};
  }
  const record = condition as Record<string, unknown>;
  return {
    fieldCode: typeof record['fieldCode'] === 'string' ? record['fieldCode'] : undefined,
    value: record['value'],
  };
}

function optionList(node: CanonicalNode): string[] {
  const cfg = node.configuration ?? {};
  for (const key of ['options', 'applicantTypes', 'statusOptions']) {
    const value = cfg[key];
    if (Array.isArray(value)) {
      return value.map((item) => coerceText(item));
    }
  }
  return [];
}

function fieldCodeOf(node: CanonicalNode): string {
  return coerceText(node.configuration?.['fieldCode']).toLowerCase();
}

function isChoiceNode(node: CanonicalNode): boolean {
  const type = nodeTypeOf(node);
  return type.includes('choice') || optionList(node).length > 0;
}

function dataSourceEndpoint(node: CanonicalNode): string {
  const dataSource = node.configuration?.['dataSource'];
  if (!dataSource || typeof dataSource !== 'object') {
    return '';
  }
  return coerceText((dataSource as Record<string, unknown>)['endpoint'] ?? '');
}

/** Existing-customer result grids — must not be treated as the opportunity list. */
function isExistingCustomerDatasetNode(node: CanonicalNode): boolean {
  const key = node.nodeKey.toLowerCase();
  if (key.startsWith('dataset_customers_')) {
    return true;
  }
  return /fetch-existing-customer/i.test(dataSourceEndpoint(node));
}

/** Create-existing customer forms load party records — not opportunity edit screens. */
export function isCreateExistingPartyFormNode(node: CanonicalNode): boolean {
  const endpoint = dataSourceEndpoint(node);
  if (/\/parties\/\{\{customerId\}\}/i.test(endpoint)) {
    return true;
  }
  const blob = labelAndKey(node);
  return blob.includes('existing') && blob.includes('create');
}

function isListNode(node: CanonicalNode): boolean {
  if (isExistingCustomerDatasetNode(node)) {
    return false;
  }
  const type = nodeTypeOf(node);
  const key = node.nodeKey.toLowerCase();
  const cfg = node.configuration ?? {};
  return (
    type.includes('datatable') ||
    type.includes('dataset') ||
    key.includes('dataset_opportunity') ||
    (Array.isArray(cfg['columns']) && Array.isArray(cfg['actions']))
  );
}

/** Pick the opportunity list dataset when the workflow defines multiple datatables. */
function resolveOpportunityListNode(nodes: CanonicalNode[]): CanonicalNode | undefined {
  const candidates = nodes.filter(isListNode);
  if (!candidates.length) {
    return undefined;
  }

  return (
    candidates.find((node) => node.nodeKey.toLowerCase().includes('dataset_opportunity')) ??
    candidates.find((node) => /\/opportunities(?:\/|\?|$)/i.test(dataSourceEndpoint(node))) ??
    candidates.find(
      (node) =>
        !/fetch-existing-customer|\/parties(?:\/|\?|$)/i.test(dataSourceEndpoint(node)),
    ) ??
    candidates[0]
  );
}

function looksLikeApplicantType(node: CanonicalNode): boolean {
  const field = fieldCodeOf(node);
  const options = optionList(node).join(' ').toLowerCase();
  return (
    field.includes('applicanttype') ||
    field.includes('applicanttypes') ||
    (options.includes('individual') && options.includes('corporate'))
  );
}

function looksLikeApplicantStatus(node: CanonicalNode): boolean {
  const field = fieldCodeOf(node);
  const options = optionList(node).join(' ').toLowerCase();
  return (
    field.includes('applicantstatus') ||
    field.includes('statusoption') ||
    options.includes('new') ||
    options.includes('existing')
  );
}

function applicantKindFromText(value: unknown): 'corporate' | 'individual' | null {
  const text = coerceText(value).toLowerCase();
  if (text.includes('corporate')) return 'corporate';
  if (text.includes('individual')) return 'individual';
  return null;
}

function isFormLike(node: CanonicalNode): boolean {
  return nodeTypeOf(node).includes('form') || node.nodeKey.toLowerCase().includes('form_');
}

function formKeysMatching(
  nodes: CanonicalNode[],
  match: (text: string, node: CanonicalNode) => boolean,
): string[] {
  return nodes.filter((node) => match(labelAndKey(node), node)).map((node) => node.nodeKey);
}

type StatusNodeKeys = { individual: string | null; corporate: string | null };

function emptyStatusNodeKeys(): StatusNodeKeys {
  return { individual: null, corporate: null };
}

function bindStatusFromTypeChoiceEdges(
  workflow: CanonicalWorkflowDocument,
  typeChoice: CanonicalNode,
  statusChoices: CanonicalNode[],
): StatusNodeKeys {
  const statusNodeKeys = emptyStatusNodeKeys();
  for (const edge of workflow.edges) {
    if (edge.sourceNodeKey !== typeChoice.nodeKey) continue;
    const target = workflow.nodes.find((node) => node.nodeKey === edge.targetNodeKey);
    const kind =
      applicantKindFromText(readCondition(edge).value) ?? applicantKindFromText(target?.label);
    if (kind && statusChoices.some((node) => node.nodeKey === edge.targetNodeKey)) {
      statusNodeKeys[kind] = edge.targetNodeKey;
    }
  }
  return statusNodeKeys;
}

function bindStatusFromNodeLabels(
  statusChoices: CanonicalNode[],
  statusNodeKeys: StatusNodeKeys,
): void {
  for (const node of statusChoices) {
    const kind = applicantKindFromText(node.label) ?? applicantKindFromText(node.nodeKey);
    if (kind && !statusNodeKeys[kind]) {
      statusNodeKeys[kind] = node.nodeKey;
    }
  }
}

function assignDefaultStatusChoices(
  statusChoices: CanonicalNode[],
  statusNodeKeys: StatusNodeKeys,
): void {
  if (!statusNodeKeys.individual && statusChoices[0]) {
    statusNodeKeys.individual = statusChoices[0].nodeKey;
  }
  if (!statusNodeKeys.corporate && statusChoices[1]) {
    statusNodeKeys.corporate = statusChoices[1].nodeKey;
  }
}

function bindStatusNodes(
  workflow: CanonicalWorkflowDocument,
  typeChoice: CanonicalNode | null,
  statusChoices: CanonicalNode[],
): StatusNodeKeys {
  const statusNodeKeys = typeChoice
    ? bindStatusFromTypeChoiceEdges(workflow, typeChoice, statusChoices)
    : emptyStatusNodeKeys();
  bindStatusFromNodeLabels(statusChoices, statusNodeKeys);
  assignDefaultStatusChoices(statusChoices, statusNodeKeys);
  return statusNodeKeys;
}

function labelAndKey(node: CanonicalNode): string {
  return `${node.nodeKey} ${node.label}`.toLowerCase();
}

function readSubmitEndpoint(node: CanonicalNode): string {
  const cfg = node.configuration ?? {};
  return coerceText(cfg['endpoint'] ?? cfg['apiEndpoint']);
}

function readSubmitMethod(node: CanonicalNode): string {
  const cfg = node.configuration ?? {};
  return coerceText(cfg['method'] ?? cfg['apiMethod']).toUpperCase();
}

function matchesApplicantKind(node: CanonicalNode, isCorporate: boolean): boolean {
  const blob = labelAndKey(node);
  return isCorporate ? blob.includes('corporate') : blob.includes('individual');
}

function isRestApiSubmitCandidate(
  node: CanonicalNode,
  isEditMode: boolean,
  isCorporate: boolean,
): boolean {
  const type = nodeTypeOf(node);
  if (type.includes('submit-form')) {
    const blob = labelAndKey(node);
    if (isEditMode) {
      return (
        matchesApplicantKind(node, isCorporate) &&
        (blob.includes('edit') || blob.includes('update'))
      );
    }
    return (
      matchesApplicantKind(node, isCorporate) &&
      blob.includes('submit') &&
      !blob.includes('edit') &&
      !blob.includes('update')
    );
  }

  if (!type.includes('rest-api')) {
    return false;
  }

  if (!matchesApplicantKind(node, isCorporate)) {
    return false;
  }

  const endpoint = readSubmitEndpoint(node);
  const method = readSubmitMethod(node);
  const blob = labelAndKey(node);

  if (isEditMode) {
    if (method && method !== 'PATCH') {
      return false;
    }
    return (
      blob.includes('update') ||
      /\{\{opportunityId\}\}/i.test(endpoint) ||
      /\/opportunities\/\{\{opportunityId\}\}/i.test(endpoint)
    );
  }

  if (method && method !== 'POST') {
    return false;
  }
  if (/\{\{opportunityId\}\}/i.test(endpoint)) {
    return false;
  }
  return blob.includes('submit') || /\/opportunities(?:\/|\?|$)/i.test(endpoint);
}

function legacySubmitNodeKey(isEditMode: boolean, isCorporate: boolean): string {
  if (isEditMode) {
    return isCorporate ? 'submit_edit_corporate' : 'submit_edit_individual';
  }
  return isCorporate ? 'submit_corporate' : 'submit_individual';
}

/** Resolve create/update submit node from workflow JSON (forms.submit-form or integrations.rest-api). */
export function discoverSubmitNodeKey(
  workflow: CanonicalWorkflowDocument | null | undefined,
  applicantType: string | null | undefined,
  isEditMode: boolean,
): string | null {
  if (!workflow?.nodes?.length) {
    return null;
  }

  const isCorporate = normalizeTypeIntent(coerceText(applicantType)) === 'corporate';
  const legacyKey = legacySubmitNodeKey(isEditMode, isCorporate);

  if (workflow.nodes.some((node) => node.nodeKey === legacyKey)) {
    return legacyKey;
  }

  const candidates = workflow.nodes.filter((node) =>
    isRestApiSubmitCandidate(node, isEditMode, isCorporate),
  );
  if (!candidates.length) {
    return null;
  }

  const preferred = candidates.find((node) => {
    const blob = labelAndKey(node);
    return isEditMode ? blob.includes('update') : blob.includes('submit');
  });
  return preferred?.nodeKey ?? candidates[0]?.nodeKey ?? null;
}

/**
 * Discover opportunity workflow roles from node types, fieldCodes, options, and edges.
 * Works for both legacy keys (choice_applicant_type) and designer keys (flow-control.*).
 */
export function discoverOpportunityWorkflowRoles(
  workflow: CanonicalWorkflowDocument | null | undefined,
): OpportunityWorkflowRoles {
  const empty: OpportunityWorkflowRoles = {
    listNodeKey: null,
    applicantTypeNodeKey: null,
    statusNodeKeys: { individual: null, corporate: null },
    searchNodeKeys: [],
    createNodeKeys: [],
    viewNodeKeys: [],
    editNodeKeys: [],
    ifElseFieldCode: null,
  };
  if (!workflow?.nodes?.length) {
    return empty;
  }

  const list = resolveOpportunityListNode(workflow.nodes);
  const typeChoice =
    workflow.nodes.find((node) => isChoiceNode(node) && looksLikeApplicantType(node)) ?? null;
  const statusChoices = workflow.nodes.filter(
    (node) => isChoiceNode(node) && looksLikeApplicantStatus(node) && node !== typeChoice,
  );

  const statusNodeKeys = bindStatusNodes(workflow, typeChoice, statusChoices);
  const formNodes = workflow.nodes.filter(isFormLike);
  const searchNodeKeys = formKeysMatching(formNodes, (text) => text.includes('search'));
  const createNodeKeys = formKeysMatching(
    formNodes,
    (text) => text.includes('create') && !text.includes('search'),
  );
  const viewNodeKeys = formKeysMatching(
    formNodes,
    (text, node) => text.includes('view') || node.configuration?.['mode'] === 'view',
  );
  const editNodeKeys = formKeysMatching(formNodes, (text, node) => {
    if (isCreateExistingPartyFormNode(node)) {
      return false;
    }
    return text.includes('edit') || node.configuration?.['mode'] === 'edit';
  });

  const ifElseNodeKeys = new Set(
    workflow.nodes
      .filter((node) => nodeTypeOf(node).includes('if-else') || nodeTypeOf(node).includes('ifelse'))
      .map((node) => node.nodeKey),
  );
  const ifElseFieldCode =
    workflow.edges
      .filter((edge) => ifElseNodeKeys.has(edge.sourceNodeKey))
      .map((edge) => readCondition(edge).fieldCode)
      .find((field) => !!field) ?? null;

  return {
    listNodeKey: list?.nodeKey ?? null,
    applicantTypeNodeKey: typeChoice?.nodeKey ?? null,
    statusNodeKeys,
    searchNodeKeys,
    createNodeKeys,
    viewNodeKeys,
    editNodeKeys,
    ifElseFieldCode,
  };
}

export type ChoiceEdgeLike = {
  id: string;
  label?: string;
  condition?: unknown;
};

export function edgeConditionValue(edge: ChoiceEdgeLike): string {
  return coerceText(readCondition(edge as CanonicalEdge).value);
}

export function isExistingStatus(value: string): boolean {
  return value.toLowerCase().includes('exist');
}

export function isNewStatus(value: string): boolean {
  const text = value.toLowerCase();
  return text.includes('new') && !text.includes('exist');
}

export function normalizeStatusIntent(value: string): 'existing' | 'new' {
  return isExistingStatus(value) ? 'existing' : 'new';
}

/** True when paused on a brand-new party create form (not existing-customer load/prefill). */
export function isNewCustomerCreateFormNodeKey(
  nodeKey: string | null | undefined,
): boolean {
  const key = coerceText(nodeKey).trim();
  if (!key || /existing/i.test(key)) {
    return false;
  }
  if (key === 'form_create_individual' || key === 'form_create_corporate') {
    return true;
  }
  return key.startsWith('form_create_') && !key.includes('search');
}

export function normalizeTypeIntent(value: string): 'corporate' | 'individual' {
  return applicantKindFromText(value) ?? 'individual';
}

export type OpportunityActionIntent =
  | 'view'
  | 'edit'
  | 'assign'
  | 'follow_up'
  | 'reject'
  | 'convert'
  | 'create'
  | 'unknown';

type PackActionRef = { key: string; label: string; type?: string };

/**
 * Map a pack action key (`action1`) or legacy key (`view_details`) to a stable intent.
 * Uses JSON label/type when the key itself is opaque.
 */
export function opportunityActionIntent(
  actionKey: string,
  actions: PackActionRef[] = [],
): OpportunityActionIntent {
  const pack = actions.find((action) => action.key === actionKey);
  const blob = `${actionKey} ${pack?.label ?? ''} ${pack?.type ?? ''}`
    .toLowerCase()
    .replaceAll('_', ' ');

  if (blob.includes('convert')) return 'convert';
  if (/\bcreate\b/.test(blob)) return 'create';
  if (blob.includes('view') || blob.includes('detail')) return 'view';
  if (blob.includes('edit')) return 'edit';
  if (blob.includes('assign')) return 'assign';
  if (blob.includes('follow')) return 'follow_up';
  if (blob.includes('reject')) return 'reject';
  return 'unknown';
}

const OMITTED_LIST_ROW_INTENTS = new Set<OpportunityActionIntent>(['create']);

/** Row "Create Opportunity" is a page-level flow, not a per-row menu item. */
export function isListCreateRowAction(
  action: PackActionRef,
  actions: PackActionRef[] = [action],
): boolean {
  return opportunityActionIntent(action.key, actions) === 'create';
}

/** Intents that must not appear in the opportunity list kebab. */
export function isOmittedOpportunityListRowAction(
  action: PackActionRef,
  actions: PackActionRef[] = [action],
): boolean {
  return OMITTED_LIST_ROW_INTENTS.has(opportunityActionIntent(action.key, actions));
}

export function resolveListActionHandle(
  actionKey: string,
  actions: Array<{ key: string; label: string; type?: string }>,
  outgoing: CanonicalEdge[],
): string | undefined {
  if (actions.some((action) => action.key === actionKey)) {
    return actionKey;
  }

  const needle = actionKey.toLowerCase().replaceAll('_', ' ');
  const byLabel = actions.find((action) => {
    const label = action.label.toLowerCase();
    if (needle.includes('create') && label.includes('create')) return true;
    if ((needle.includes('view') || needle.includes('detail')) && label.includes('view')) {
      return true;
    }
    if (needle.includes('edit') && label.includes('edit')) return true;
    if (needle.includes('assign') && label.includes('assign')) return true;
    if (needle.includes('follow') && label.includes('follow')) return true;
    if (needle.includes('reject') && label.includes('reject')) return true;
    if (needle.includes('convert') && label.includes('convert')) return true;
    return false;
  });
  if (byLabel) {
    return byLabel.key;
  }

  const byHandle = outgoing.find((edge) => edge.sourceHandle === actionKey);
  return byHandle?.sourceHandle;
}

export function pickChoiceEdge<T extends ChoiceEdgeLike>(
  edges: T[],
  expected: string,
  preferTarget?: (edgeId: string) => boolean,
): T | undefined {
  const expectedLower = expected.toLowerCase();
  const byValue = edges.filter((edge) => {
    const value = edgeConditionValue(edge).toLowerCase();
    return value === expectedLower || value.includes(expectedLower) || expectedLower.includes(value);
  });
  if (preferTarget) {
    const preferred = (byValue.length ? byValue : edges).find((edge) => preferTarget(edge.id));
    if (preferred) return preferred;
  }
  return byValue[0] ?? edges[0];
}
